import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { doc, setDoc, writeBatch, deleteDoc, collection, query, where, onSnapshot } from 'firebase/firestore';
import { calculateOVR, blendScoutingOVR, seasonYearOf } from '../lib/utils';
import { calculateXOVR } from '../lib/xovr-engine';
import { startNewSeason, seasonKey } from '../lib/season-rollover';
import { db } from '../firebase';
import { showSaveError, showToast } from './Toast';
import { benchLogRebuildForSource } from '../lib/game-positions';
import { planClearSeasonStats, clearSeasonStats } from '../lib/clear-season-stats';
import { fieldTimeSourceOf, type FieldTimeSource } from '../lib/gc-innings';
import { tapLight, setHapticsEnabled } from '../lib/haptics';
import { 
  Shield, 
  ChevronDown, 
  ChevronUp, 
  Calendar, 
  FileText, 
  Plus, 
  X,
  Settings as SettingsIcon,
  Users,
  Clock,
  MapPin,
  Activity,
  Zap,
  Target,
  Timer,
  Heart,
  Upload,
  User,
  AlertTriangle,
  Trash2,
  Archive,
  ArrowRight,
  Copy,
  Check as CheckIcon,
  KeyRound,
  Lock,
  Sparkles
} from 'lucide-react';
import { cn } from '../lib/utils';
import { CalendarImporter } from './CalendarImporter';
import { SupportForm } from './SupportForm';
import { TermsOfService, PrivacyPolicy } from './LegalModals';
import { AdminTicketPanel } from './AdminTicketPanel';
import { AdminUsagePanel } from './AdminUsagePanel';
import { PITCH_COUNT_RULESETS } from '../lib/pitch-count-rules';
import { ORDER_MODES, ORDER_MODE_DESCRIPTIONS } from '../lib/batting-order';
import { normalizeBattingOrderStyle } from '../lib/lineup-engine';
import { RestThreshold } from '../types/pitch-rules';

const ORG_OPTIONS = [
  { value: 'little_league',  label: 'Little League' },
  { value: 'usssa',          label: 'USSSA' },
  { value: 'perfect_game',   label: 'Perfect Game' },
  { value: 'nfhs',           label: 'NFHS (High School)' },
  { value: 'independent',    label: 'Independent / Rec' },
];

const ORG_DIVISIONS: Record<string, string[]> = {
  little_league: ['Minor', 'Major', 'Junior', 'Senior'],
  usssa:         ['A', 'AA', 'AAA', 'Major', 'Futures'],
  perfect_game:  ['Open', 'Premier', 'Elite'],
  nfhs:          ['JV', 'Varsity'],
  independent:   ['Rec', 'Competitive', 'Travel'],
};

function normalizeOrg(org: string): string {
  if (org === 'littleleague') return 'little_league';
  if (org === 'perfectgame')  return 'perfect_game';
  return org || '';
}

const timezones = [
  { label: "(GMT-10:00) Hawaii", value: "Pacific/Honolulu" },
  { label: "(GMT-09:00) Alaska", value: "America/Anchorage" },
  { label: "(GMT-08:00) Pacific Time", value: "America/Los_Angeles" },
  { label: "(GMT-07:00) Arizona", value: "America/Phoenix" },
  { label: "(GMT-07:00) Mountain Time", value: "America/Denver" },
  { label: "(GMT-06:00) Central Time", value: "America/Chicago" },
  { label: "(GMT-05:00) Eastern Time", value: "America/New_York" },
  { label: "(GMT-04:00) Puerto Rico", value: "America/Puerto_Rico" }
];

// Age Group is a fixed dropdown. It drives Style of Play: 9U and older are
// kid-pitch, so Style is forced to 'playerspitch' (full engine). 5U–8U still
// choose their format. Values 15U/16U/HS* map to the pitch-count rulesets.
const AGE_GROUPS = ['5U','6U','7U','8U','9U','10U','11U','12U','13U','14U','15U','16U','HS JV','HS Varsity'];
const YOUNG_AGES = new Set(['5U','6U','7U','8U']); // formats other than player-pitch are possible here
// True when the age group implies kid-pitch (9U and up, including HS).
const isPlayerPitchAge = (age: string) => age !== '' && !YOUNG_AGES.has(age);

const Section = ({ id, title, subtitle, children, badge, expandedSections, setExpandedSections }: any) => {
  const isExpanded = expandedSections.has(id);

  return (
    <div className="bg-surface rounded-card shadow-card overflow-hidden mb-4">
      <button
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setExpandedSections((prev: Set<string>) => {
          const next = new Set(prev);
          next.has(id) ? next.delete(id) : next.add(id);
          return next;
        })}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-raised transition-colors"
      >
        <div className="flex items-center space-x-3 min-w-0 flex-1">
          <span className="font-black uppercase text-sm text-ink shrink-0">{title}</span>
          {subtitle && !isExpanded && (
            <span className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest truncate">{subtitle}</span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {badge && (
            <span className="px-2 py-0.5 bg-raised text-ink-2 text-[10px] font-bold rounded uppercase tracking-wider">
              {badge}
            </span>
          )}
          {isExpanded ? <ChevronUp className="w-5 h-5 text-ink-2" /> : <ChevronDown className="w-5 h-5 text-ink-2" />}
        </div>
      </button>
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            <div className="px-6 pb-6 pt-2 border-t border-hairline">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Toggle = ({ label, description, checked, onChange, children }: any) => (
  <div className="py-4 border-b border-hairline last:border-0">
    <div className="flex items-center justify-between">
      <div>
        <div className="font-black uppercase text-sm text-ink">{label}</div>
        {description && <div className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest mt-1">{description}</div>}
      </div>
      <button
        onClick={() => { tapLight(); onChange(!checked); }}
        className={cn(
          // Track: emerald accent ON (stable in light+dark), themed gray OFF.
          // Avoid arbitrary hexes / bg-ink here — the .light theme-swap can't
          // remap them, which made the OFF track + knob look wrong in dark mode.
          "w-12 h-6 rounded-full transition-colors relative flex-shrink-0",
          checked ? "bg-emerald-500" : "bg-track"
        )}
      >
        <div className={cn(
          // Literal white knob (not bg-ink) so it stays white in both themes.
          "w-4 h-4 bg-surface rounded-full absolute top-1 transition-transform shadow-sm",
          checked ? "translate-x-7" : "translate-x-1"
        )} />
      </button>
    </div>
    {checked && children && (
      <div className="mt-4 pl-4 border-l-2 border-hairline">
        {children}
      </div>
    )}
  </div>
);

const InputField = ({ label, value, onChange, type = "text", placeholder, options }: any) => (
  <div className="space-y-1.5">
    <label className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">{label}</label>
    {options ? (
      <select 
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-surface border border-hairline rounded-xl px-4 py-2 text-ink font-black outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
      >
        <option value="">Select...</option>
        {options.map((opt: any) => (
          typeof opt === 'string' ? (
            <option key={opt} value={opt}>{opt}</option>
          ) : (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          )
        ))}
      </select>
    ) : (
      <input 
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-surface border border-hairline rounded-xl px-4 py-2 text-ink font-black outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
      />
    )}
  </div>
);

export const SettingsView = ({ team, players, seasonGames = [], onboardingStatsGame = null, isHeadCoach = true, userEmail = '', uid = '', isAdmin = false, onOpenSeasonHistory, onOpenWrapped }: { team: any, players: any[], seasonGames?: any[], onboardingStatsGame?: any, isHeadCoach?: boolean, userEmail?: string, uid?: string, isAdmin?: boolean, onOpenSeasonHistory?: () => void, onOpenWrapped?: () => void }) => {
  const [copiedCode, setCopiedCode] = useState(false);
  const [isShowingSupportForm, setIsShowingSupportForm] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [joinCode, setJoinCode] = useState<string>(team.joinCode || '');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [isImportingCalendar, setIsImportingCalendar] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  // Style of Play is read-only by default (harder to change than a casual dropdown);
  // the 5U–8U "Edit" link unlocks the picker. 9U+ is always forced/locked.
  const [styleEditUnlocked, setStyleEditUnlocked] = useState(false);
  const [myTickets, setMyTickets] = useState<any[]>([]);

  useEffect(() => {
    if (!uid) return;
    const q = query(collection(db, 'supportTickets'), where('uid', '==', uid));
    const unsub = onSnapshot(q, snap => {
      const docs = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter((t: any) => t.status === 'open' || t.status === 'in_progress')
        .sort((a: any, b: any) => (b.createdAt > a.createdAt ? 1 : -1));
      setMyTickets(docs);
    }, () => {});
    return unsub;
  }, [uid]);
  // Profile editing now lives in the account-level ManageAccountModal (reachable from the
  // avatar menu), so it's available to assistant coaches too. It was removed from Team Settings.

  const [isEditingThresholds, setIsEditingThresholds] = useState(false);
  const [isClearingStats, setIsClearingStats] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showDeleteTeamConfirm, setShowDeleteTeamConfirm] = useState(false);
  const [isDeletingTeam, setIsDeletingTeam] = useState(false);
  const [deleteTeamError, setDeleteTeamError] = useState<string | null>(null);

  // ── "Start New Season" rollover (preservative, not destructive) ────────────
  const [showNewSeasonModal, setShowNewSeasonModal] = useState(false);
  const [newSeasonStep, setNewSeasonStep] = useState<1 | 2 | 3>(1);
  const [newSeasonName, setNewSeasonName] = useState<string>('Summer');
  const [newSeasonYear, setNewSeasonYear] = useState<number>(new Date().getFullYear());
  // Kids age up between seasons — the wizard asks the coach to verify the level
  // of play for the new season (defaults to the current age group).
  const [newSeasonAgeGroup, setNewSeasonAgeGroup] = useState<string>('');
  const [isRollingSeason, setIsRollingSeason] = useState(false);
  const [rolloverError, setRolloverError] = useState<string | null>(null);
  const [rolloverDone, setRolloverDone] = useState(false);
  const [teamInfo, setTeamInfo] = useState({
    name: team.name || '',
    sport: team.sport || 'Baseball',
    defaultInnings: team.defaultInnings || 6,
    org: normalizeOrg(team.org || ''),
    league: team.league || '',
    division: team.division || '',
    zipCode: team.zipCode || '',
    season: team.season || 'Spring',
    // Always a NUMBER. The rollover engine writes a number, and gamesForSeason
    // compares numerically — a string here would be re-written over the team doc
    // by updateTeamInfo (which persists this whole object) on any Team Info edit.
    seasonYear: seasonYearOf(team) ?? new Date().getFullYear(),
    timezone: team.timezone || '',
    ageGroup: team.ageGroup || '',
    level: team.level || '',
    logoURL: team.logoURL || '',
    // Style of Play gates major features (pitch counts, xOVR, scouting matrix).
    // Set at onboarding; now editable here. Default matches onboarding's default.
    styleOfPlay: team.styleOfPlay || 'playerspitch',
  });

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    setIsUploading(true);
    try {
      // Read the file as a data URL
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = async () => {
          // Resize the image to a max of 200x200 to save space in Firestore
          const canvas = document.createElement('canvas');
          const MAX_SIZE = 200;
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > MAX_SIZE) {
              height *= MAX_SIZE / width;
              width = MAX_SIZE;
            }
          } else {
            if (height > MAX_SIZE) {
              width *= MAX_SIZE / height;
              height = MAX_SIZE;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0, width, height);
            const base64String = canvas.toDataURL('image/jpeg', 0.8);
            await updateTeamInfo('logoURL', base64String);
          }
          setIsUploading(false);
        };
        img.src = event.target?.result as string;
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Failed to process logo", error);
      setIsUploading(false);
    }
  };

  const [settings, setSettings] = useState({
    // Field Time Equalizer — single master toggle (replaces fieldTimeEnabled + fairnessEngineEnabled)
    fieldTimeEqualizerEnabled: team.settings?.fieldTimeEqualizerEnabled ?? team.settings?.fieldTimeEnabled ?? team.settings?.fairnessEngineEnabled ?? false,
    minFieldInningsEnabled: team.settings?.minFieldInningsEnabled ?? false,
    minFieldInningsPerPlayer: team.settings?.minFieldInningsPerPlayer ?? 3,
    equalBenchInnings: team.settings?.equalBenchInnings ?? false,
    noConsecutiveBench: team.settings?.noConsecutiveBench ?? false,
    noConsecutivePosition: team.settings?.noConsecutivePosition ?? false,
    mustPlayInfieldEnabled: team.settings?.mustPlayInfieldEnabled ?? false,
    mustPlayInfieldInnings: team.settings?.mustPlayInfieldInnings ?? 1,
    fieldTimeSource: fieldTimeSourceOf(team) as FieldTimeSource,
    fourOutfielders: team.settings?.fourOutfielders ?? false,
    noPitcher: team.settings?.noPitcher ?? false,
    noCatcher: team.settings?.noCatcher ?? false,
    // Batting — legacy 'Everyone Bats'/'Traditional 9 Players' migrate to the new
    // Continuous / 10 Players / 9 Players taxonomy (see normalizeBattingOrderStyle).
    battingOrderStyle: normalizeBattingOrderStyle(team.settings),
    useDH: team.settings?.useDH ?? false,
    // Canonical batting-strategy keys are the ones the lineup engine
    // (sortBattingOrder) actually understands — the same list the live Lineup
    // Builder shows. Legacy stored values are migrated to their engine
    // equivalent so old teams aren't stuck on a strategy that silently no-ops:
    //   'Equal ABs' → 'Fairness'   (engine had no 'Equal ABs' case)
    //   'Power'     → 'Optimized'  (engine never implemented 'Power')
    battingStrategy: (() => {
      const stored = team.settings?.battingStrategy;
      if (stored === 'Equal ABs') return 'Fairness';
      if (stored === 'Power') return 'Optimized';
      return stored ?? 'Fairness';
    })(),
    mandatoryAB: team.settings?.mandatoryAB ?? false,
    showRecordOnDashboard: team.settings?.showRecordOnDashboard ?? true,
    showTeamOvrInHeader: team.settings?.showTeamOvrInHeader ?? true,
    // Pitching
    maxPitchesPerDayEnabled: team.settings?.maxPitchesPerDayEnabled ?? false,
    maxPitchesPerDay: team.settings?.maxPitchesPerDay ?? 75,
    maxInningsPerGameEnabled: team.settings?.maxInningsPerGameEnabled ?? false,
    maxInningsPerGame: team.settings?.maxInningsPerGame ?? 3,
    maxInningsPerDayEnabled: team.settings?.maxInningsPerDayEnabled ?? false,
    maxInningsPerDay: team.settings?.maxInningsPerDay ?? 6,
    maxInningsPerWeekEnabled: team.settings?.maxInningsPerWeekEnabled ?? false,
    maxInningsPerWeek: team.settings?.maxInningsPerWeek ?? 9,
    noConsecutiveDaysPitching: team.settings?.noConsecutiveDaysPitching ?? false,
    maxAppearancesPer3DaysEnabled: team.settings?.maxAppearancesPer3DaysEnabled ?? false,
    maxAppearancesPer3Days: team.settings?.maxAppearancesPer3Days ?? 2,
    tournamentsOperateIndependently: team.settings?.tournamentsOperateIndependently ?? false,
    // Position Restrictions
    catcherToPitcherRestriction: team.settings?.catcherToPitcherRestriction ?? false,
    pitcherToCatcherRestriction: team.settings?.pitcherToCatcherRestriction ?? false,
    pitchCountBlocksCatchingEnabled: team.settings?.pitchCountBlocksCatchingEnabled ?? false,
    pitchCountBlocksCatching: team.settings?.pitchCountBlocksCatching ?? 40,
    noPitcherReentry: team.settings?.noPitcherReentry ?? false,
    excludeCatcherFromBullpen: team.settings?.excludeCatcherFromBullpen ?? false,
    // Rest & Recovery
    restThresholds: team.settings?.restThresholds || [
      { minPitches: 0,  maxPitches: 20,  restDays: 0 },
      { minPitches: 21, maxPitches: 35,  restDays: 1 },
      { minPitches: 36, maxPitches: 50,  restDays: 2 },
      { minPitches: 51, maxPitches: 65,  restDays: 3 },
      { minPitches: 66, maxPitches: 9999, restDays: 4 },
    ],
    // Game Day
    trackGameBalls: team.settings?.trackGameBalls ?? true,
    preGameRemindersEnabled: team.settings?.preGameRemindersEnabled ?? true,
    postGameNudgesEnabled: team.settings?.postGameNudgesEnabled ?? true,
    hapticsEnabled: team.settings?.hapticsEnabled ?? true,
    latePlayerTracking: team.settings?.latePlayerTracking ?? true,
    scrimmagePitchCounts: team.settings?.scrimmagePitchCounts ?? false,
    // Game Clock (opt-in countdown timer for timed games)
    gameClockEnabled: team.settings?.gameClockEnabled ?? false,
    gameClockPromptSeen: team.settings?.gameClockPromptSeen ?? false,
    defaultGameClockMinutes: team.settings?.defaultGameClockMinutes ?? 75,
    pitchCountRuleset: team.settings?.pitchCountRuleset || (team.rules === 'Little League' ? 'little_league' : 
                       team.rules === 'USSSA' ? 'usssa' : 
                       team.rules === 'PerfectGame' ? 'perfect_game' : 'little_league'),
    pitchCountAgeGroup: team.settings?.pitchCountAgeGroup || team.ageGroup || '12U',
    enforcePitchCounts: team.settings?.enforcePitchCounts ?? (team.rules === 'Little League' || !team.rules),
    enforceGlobalRest: team.settings?.enforceGlobalRest ?? false,
    entireSeasonLineup: team.settings?.entireSeasonLineup ?? false,
    customRules: team.settings?.customRules || {
      dailyMax: 85,
      restThresholds: [
        { minPitches: 1, maxPitches: 20, daysRestRequired: 0 },
        { minPitches: 21, maxPitches: 35, daysRestRequired: 1 },
        { minPitches: 36, maxPitches: 50, daysRestRequired: 2 },
        { minPitches: 51, maxPitches: 65, daysRestRequired: 3 },
        { minPitches: 66, maxPitches: 85, daysRestRequired: 4 },
      ]
    },
    // Tournament Org
    tournamentOrg: team.settings?.tournamentOrg || 'little_league',
    customTournamentTracking: team.settings?.customTournamentTracking || 'pitches',
    customTournamentDailyLimit: team.settings?.customTournamentDailyLimit || 85,
    customTournamentEventLimit: team.settings?.customTournamentEventLimit || 0,
    customTournamentNoRestDays: team.settings?.customTournamentNoRestDays ?? false,
    // Scouting
    includeAssistantScouting: team.settings?.includeAssistantScouting ?? false,
  });

  // Reconcile legacy data for 9U+ teams (kid-pitch), correcting state set wrong at
  // onboarding or pre-dating the age→style rule, so the app's feature gating matches
  // what the UI now shows. Two things get fixed once, only on a real mismatch:
  //   1. styleOfPlay forced to 'playerspitch'.
  //   2. Field Config flags (4 outfielders / no pitcher / no catcher) cleared — that
  //      section is hidden for 9U+, so stale 'true' values would otherwise be stuck on.
  // Guarded to head coaches (assistants can't write team settings).
  useEffect(() => {
    if (!isHeadCoach) return;
    if (!isPlayerPitchAge(teamInfo.ageGroup)) return;

    const write: any = {};
    if (teamInfo.styleOfPlay !== 'playerspitch') {
      write.styleOfPlay = 'playerspitch';
      setTeamInfo(prev => ({ ...prev, styleOfPlay: 'playerspitch' }));
    }
    if (settings.fourOutfielders || settings.noPitcher || settings.noCatcher) {
      const cleared = { ...settings, fourOutfielders: false, noPitcher: false, noCatcher: false };
      write.settings = cleared;
      setSettings(cleared);
    }
    if (Object.keys(write).length > 0) {
      setDoc(doc(db, 'teams', team.id), write, { merge: true })
        .catch(err => console.error('Failed to reconcile age-group config', err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team.id]);

  useEffect(() => {
    if (isHeadCoach && !team.joinCode) {
      const code = Math.random().toString(36).substring(2, 8).toUpperCase();
      // One atomic batch: the team's joinCode field and the /joinCodes lookup
      // doc must never diverge, or the displayed code won't work for joining.
      const batch = writeBatch(db);
      batch.set(doc(db, 'teams', team.id), { joinCode: code }, { merge: true });
      batch.set(doc(db, 'joinCodes', code), { teamId: team.id, createdAt: new Date().toISOString() });
      batch.commit().catch(console.error);
      setJoinCode(code);
    }
  }, []);

  const updateTeamInfo = async (key: string, value: any) => {
    const newInfo = { ...teamInfo, [key]: value };
    setTeamInfo(newInfo);
    try {
      const writeData: any = { ...newInfo };
      if (key === 'org') writeData.org = value;
      await setDoc(doc(db, 'teams', team.id), writeData, { merge: true });
      if (key === 'org' && settings.enforcePitchCounts && value && value !== 'independent') {
        await updateSetting('pitchCountRuleset', value);
      }
    } catch (error) {
      // The local state above already flipped (optimistic UI) — tell the coach
      // the change didn't stick or it'll silently revert on next load.
      showSaveError('That team info change', error);
    }
  };

  // Age Group drives Style of Play. 9U+ is kid-pitch → force Players Pitch (full
  // engine). 5U–8U keep whatever style they had. Both keys are written together so
  // the team doc stays consistent in one round-trip. `extraInfo` lets a caller fold
  // additional team-info fields into the same write — the season wizard uses it so
  // the write carries the NEW season instead of clobbering it with stale form state.
  const updateAgeGroup = async (newAge: string, extraInfo: Record<string, any> = {}) => {
    const forcePlayerPitch = isPlayerPitchAge(newAge);
    const newInfo = {
      ...teamInfo,
      ...extraInfo,
      ageGroup: newAge,
      ...(forcePlayerPitch ? { styleOfPlay: 'playerspitch' } : {}),
    };
    setTeamInfo(newInfo);
    setStyleEditUnlocked(false); // re-lock the style picker on any age change

    // Field Configuration (4 outfielders / no pitcher / no catcher) only applies to
    // 5U–8U. Moving up to 9U+ hides that section, so clear those flags too — otherwise
    // a team could silently keep "no catcher" with no visible toggle to undo it.
    const clearFieldConfig = forcePlayerPitch &&
      (settings.fourOutfielders || settings.noPitcher || settings.noCatcher);
    const newSettings = clearFieldConfig
      ? { ...settings, fourOutfielders: false, noPitcher: false, noCatcher: false }
      : settings;
    if (clearFieldConfig) setSettings(newSettings);

    try {
      await setDoc(doc(db, 'teams', team.id), {
        ...newInfo,
        ...(clearFieldConfig ? { settings: newSettings } : {}),
      }, { merge: true });
    } catch (error) {
      showSaveError('That age group change', error);
    }
  };

  const updateSetting = async (key: string, value: any) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    try {
      await setDoc(doc(db, 'teams', team.id), {
        settings: newSettings
      }, { merge: true });
    } catch (error) {
      showSaveError('That setting', error);
    }
  };

  // "Field positions come from" also rewrites this season's bench counts, since
  // each player's benchLog drives Field Time Fairness and an import used to
  // overwrite it even where the Dugout live log was right (support log G9).
  // Only games with BOTH a complete live log and GC field time change. One
  // batch, so the setting and the counts it implies can never disagree.
  const updateFieldTimeSource = async (value: FieldTimeSource) => {
    if (value === settings.fieldTimeSource) return;
    const newSettings = { ...settings, fieldTimeSource: value };
    setSettings(newSettings);
    try {
      const { updates, gamesAffected } = benchLogRebuildForSource(players, seasonGames, value, teamInfo.defaultInnings || 6);
      const now = new Date().toISOString();
      const batch = writeBatch(db);
      batch.set(doc(db, 'teams', team.id), { settings: newSettings }, { merge: true });
      updates.forEach(u => batch.update(doc(db, `teams/${team.id}/players`, u.id), {
        benchLog: u.benchLog,
        seasonBenchInnings: u.seasonBenchInnings,
        updatedAt: now,
      }));
      await batch.commit();
      if (gamesAffected > 0) {
        showToast(`Field time updated for ${gamesAffected} ${gamesAffected === 1 ? 'game' : 'games'}`);
      }
    } catch (error) {
      showSaveError('That setting', error);
    }
  };

  const handleDeleteTeam = async () => {
    setIsDeletingTeam(true);
    setDeleteTeamError(null);
    try {
      await deleteDoc(doc(db, 'teams', team.id));
    } catch (error: any) {
      console.error('Failed to delete team', error);
      setDeleteTeamError(error?.message ?? 'Delete failed. Check your connection.');
      setIsDeletingTeam(false);
    }
  };

  // Clear This Season's Stats: everything the Stats tab, ratings and pitcher
  // rest read for the current season, not just the player docs (support log G1).
  const seasonLabel = team.season && team.seasonYear ? `${team.season} ${team.seasonYear}` : 'this season';
  const clearPlan = showClearConfirm
    ? planClearSeasonStats({ team, players, seasonGames, signupGame: onboardingStatsGame })
    : null;

  const handleClearAllStats = async () => {
    setIsClearingStats(true);
    try {
      await clearSeasonStats(db, team.id, planClearSeasonStats({ team, players, seasonGames, signupGame: onboardingStatsGame }));
      setShowClearConfirm(false);
      showToast(team.season && team.seasonYear ? `${seasonLabel} stats cleared` : "This season's stats cleared");
    } catch (error) {
      showSaveError('Clearing this season\'s stats', error);
    } finally {
      setIsClearingStats(false);
    }
  };

  // Whether the chosen new-season key collides with an existing one (current or
  // a previously-archived season). Blocks Confirm so an archive can't be clobbered.
  const archivedKeys = new Set<string>(
    players.flatMap((p: any) => Object.keys(p?.seasonStats ?? {}))
  );
  // Coerce the year: an older team doc may store seasonYear as a string, which
  // would build a key that never matches the numeric ones in seasonStats.
  const currentKey = team?.season ? seasonKey(team.season, seasonYearOf(team) ?? 0) : '';
  const targetKey = seasonKey(newSeasonName, newSeasonYear);
  const targetIsCurrent = targetKey === currentKey;
  const targetAlreadyArchived = archivedKeys.has(targetKey);

  const handleStartNewSeason = async () => {
    setIsRollingSeason(true);
    setRolloverError(null);
    try {
      await startNewSeason({
        db,
        team,
        players,
        newSeason: newSeasonName,
        newYear: newSeasonYear,
      });
      // Sync the Team Info form with what the wizard just wrote. The form's
      // local state was initialized on mount, and updateTeamInfo writes the
      // WHOLE form object — without this sync, the next Team Info edit would
      // silently write the OLD season back over the rollover.
      // Number, not String: startNewSeason wrote a number to the team doc, and
      // this object gets persisted verbatim by the next updateTeamInfo call.
      const seasonInfo = { season: newSeasonName, seasonYear: newSeasonYear };
      if (newSeasonAgeGroup && newSeasonAgeGroup !== teamInfo.ageGroup) {
        // One write carries the new age group AND the new season together
        // (also handles the kid-pitch style force + field-config cleanup).
        await updateAgeGroup(newSeasonAgeGroup, seasonInfo);
      } else {
        setTeamInfo(prev => ({ ...prev, ...seasonInfo }));
      }
      setRolloverDone(true);
    } catch (error: any) {
      console.error('Failed to start new season', error);
      setRolloverError(error?.message ?? 'Rollover failed. Check your connection and try again.');
    } finally {
      setIsRollingSeason(false);
    }
  };

  const openNewSeasonModal = () => {
    // Default the new season to the next logical one and reset wizard state.
    // seasonYear can arrive as a string from older team docs — coerce to Number
    // so the +1 below is arithmetic, not string concatenation.
    const order = ['Spring', 'Summer', 'Fall', 'Winter'];
    const idx = order.indexOf(team?.season || 'Spring');
    const nextName = order[(idx + 1) % order.length];
    const curYear = Number(team?.seasonYear) || new Date().getFullYear();
    const nextYear = nextName === 'Spring' ? curYear + 1 : curYear;
    setNewSeasonName(nextName);
    setNewSeasonYear(nextYear);
    setNewSeasonAgeGroup(teamInfo.ageGroup || team?.ageGroup || '');
    setNewSeasonStep(1);
    setRolloverError(null);
    setRolloverDone(false);
    setShowNewSeasonModal(true);
  };

  if (!isHeadCoach) {
    return (
      <div className="min-h-screen bg-canvas p-6 pb-24">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-black italic uppercase text-ink">Settings</h1>
            <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest mt-1">{team.name}</p>
          </div>

          {/* Display Settings */}
          <Section id="display" title="Display Settings" expandedSections={expandedSections} setExpandedSections={setExpandedSections}>
            <div className="space-y-6">
              {/* Dark Mode toggle hidden until real dark mode ships. The app moved to
                  inline hex colors that the .light theme-swap can't reach, so the
                  toggle did almost nothing. Restore this block (and the duplicate
                  below) once a theme-aware token layer makes dark mode actually work. */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="block text-[10px] font-semibold text-ink-2 uppercase tracking-widest mb-1">Show Record on Dashboard</label>
                  <p className="text-[10px] text-ink-2">Display the team's W-L-T record on the home page.</p>
                </div>
                <button
                  onClick={() => updateSetting('showRecordOnDashboard', !settings.showRecordOnDashboard)}
                  className={cn("w-12 h-6 rounded-full transition-colors relative", settings.showRecordOnDashboard ? "bg-emerald-500" : "bg-track")}
                >
                  <div className={cn("w-4 h-4 bg-surface rounded-full absolute top-1 transition-transform", settings.showRecordOnDashboard ? "translate-x-7" : "translate-x-1")} />
                </button>
              </div>
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <label className="block text-[10px] font-semibold text-ink-2 uppercase tracking-widest mb-1">Show Team OVR in Header</label>
                  <p className="text-[10px] text-ink-2">Display the team's average OVR or xOVR badge in the top bar.</p>
                </div>
                <button
                  onClick={() => updateSetting('showTeamOvrInHeader', !settings.showTeamOvrInHeader)}
                  className={cn("w-12 h-6 rounded-full transition-colors relative shrink-0 ml-4", settings.showTeamOvrInHeader ? "bg-emerald-500" : "bg-track")}
                >
                  <div className={cn("w-4 h-4 bg-surface rounded-full absolute top-1 transition-transform", settings.showTeamOvrInHeader ? "translate-x-7" : "translate-x-1")} />
                </button>
              </div>
            </div>
          </Section>

          {/* Calendar Import */}
          <Section id="calendar" title="Calendar Import" expandedSections={expandedSections} setExpandedSections={setExpandedSections}>
            <div className="text-center py-8">
              <Calendar className="w-12 h-12 text-hairline mx-auto mb-4" />
              <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Connect your team calendar to sync games and practices automatically.</p>
              <button
                onClick={() => setIsImportingCalendar(true)}
                className="mt-4 px-6 py-2 bg-surface border border-hairline rounded-xl text-[10px] font-bold text-ink-2 uppercase tracking-widest hover:text-ink transition-colors"
              >
                Connect Calendar
              </button>
            </div>
          </Section>

          {/* Help & Support */}
          <Section id="support" title="Help & Support" subtitle="Report a bug or send feedback" expandedSections={expandedSections} setExpandedSections={setExpandedSections}>
            <div className="py-2">
              <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest mb-4">
                Found a bug? Have a feature idea? Send us a message and we'll get back to you.
              </p>
              <button
                onClick={() => setIsShowingSupportForm(true)}
                className="px-6 py-3 bg-surface border border-hairline rounded-xl font-black text-xs uppercase tracking-widest text-ink-2 hover:text-ink hover:border-ink-mute transition-colors"
              >
                Contact Support
              </button>
            </div>
          </Section>

        </div>

        {isImportingCalendar && (
          <CalendarImporter
            teamId={team.id}
            teamName={team.name}
            onClose={() => setIsImportingCalendar(false)}
          />
        )}
        {isShowingSupportForm && (
          <SupportForm
            teamId={team.id}
            teamName={team.name}
            userEmail={userEmail}
            uid={uid}
            onClose={() => setIsShowingSupportForm(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas p-6 pb-24">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-black italic uppercase text-ink">
            Team Settings
          </h1>
          <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest mt-1">Configure rules and preferences for {team.name}</p>
        </div>

        {/* Team Information */}
        <Section
          id="team-info"
          title="Team Information" 
          subtitle={`${teamInfo.name} · ${teamInfo.ageGroup} · ${teamInfo.level}`}
          expandedSections={expandedSections}
          setExpandedSections={setExpandedSections}
        >
          <div className="space-y-4">
            {/* Logo + name on one row. The logo is a small tap-target: tapping the
                image opens the file picker (no separate upload button needed). */}
            <div className="flex items-end gap-3">
              <label className="group relative shrink-0 cursor-pointer" title="Tap to change logo">
                <div className="w-14 h-14 bg-surface rounded-xl border border-hairline flex items-center justify-center overflow-hidden group-hover:border-ink-mute transition-colors">
                  {teamInfo.logoURL ? (
                    <img src={teamInfo.logoURL} alt="Team Logo" className="w-full h-full object-cover" />
                  ) : (
                    <Shield className="w-6 h-6 text-ink-2" />
                  )}
                  {/* Hover/upload affordance overlay */}
                  <div className="absolute inset-0 bg-surface/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-xl">
                    <Upload className="w-4 h-4 text-surface" />
                  </div>
                  {isUploading && (
                    <div className="absolute inset-0 bg-surface/50 flex items-center justify-center rounded-xl">
                      <span className="text-[8px] font-semibold text-surface uppercase tracking-wider">…</span>
                    </div>
                  )}
                </div>
                <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} disabled={isUploading} />
              </label>
              <div className="flex-1 min-w-0">
                <InputField
                  label="Team Name"
                  value={teamInfo.name}
                  onChange={(v: string) => updateTeamInfo('name', v)}
                />
              </div>
            </div>

            {/* Sport field removed — nothing in the app read team.sport, and softball
                ships as its own app (config-fork on the same backend), so a per-team
                sport picker is meaningless. Old Firestore values are left untouched. */}
            <div className="grid grid-cols-2 gap-4">
              <InputField
                label="Age Group"
                value={teamInfo.ageGroup}
                onChange={(v: string) => updateAgeGroup(v)}
                options={AGE_GROUPS}
              />
              <InputField
                label="Competition Level"
                value={teamInfo.level}
                onChange={(v: string) => updateTeamInfo('level', v)}
                placeholder="e.g. AAA / Major"
              />
            </div>

            {/* Style of Play — gates pitch counts / xOVR / scouting.
                · 9U+  → kid-pitch is assumed; locked to Players Pitch (read-only + note).
                · 5U–8U → still a choice, but read-only behind an "Edit" link so it's
                          harder to change than a casual dropdown. */}
            {(() => {
              const styleLabels: Record<string, string> = {
                tballcoachpitch: 'T-Ball / Coach Pitch',
                machinepitch: 'Machine Pitch',
                playerspitch: 'Players Pitch',
              };
              const isLockedByAge = isPlayerPitchAge(teamInfo.ageGroup);
              const currentLabel = styleLabels[teamInfo.styleOfPlay] || 'Players Pitch';

              if (isLockedByAge) {
                return (
                  <div>
                    <label className="block text-[10px] font-semibold text-ink-2 uppercase tracking-widest mb-1">Style of Play</label>
                    <div className="w-full bg-raised border border-hairline rounded-xl px-4 py-2 text-ink-2 font-black">
                      Players Pitch
                    </div>
                    <p className="text-[10px] text-ink-2 mt-1">
                      {teamInfo.ageGroup} plays kid-pitch, so pitch counts, xOVR &amp; reliever intel are on.
                    </p>
                  </div>
                );
              }

              // 5U–8U: read-only with an Edit link; unlock reveals the picker.
              if (!styleEditUnlocked) {
                return (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Style of Play</label>
                      <button
                        onClick={() => setStyleEditUnlocked(true)}
                        className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest hover:text-ink transition-colors"
                      >
                        Edit
                      </button>
                    </div>
                    <div className="w-full bg-raised border border-hairline rounded-xl px-4 py-2 text-ink font-black">
                      {currentLabel}
                    </div>
                  </div>
                );
              }

              return (
                <div>
                  <InputField
                    label="Style of Play"
                    value={teamInfo.styleOfPlay}
                    onChange={(v: string) => { updateTeamInfo('styleOfPlay', v); setStyleEditUnlocked(false); }}
                    options={[
                      { value: 'tballcoachpitch', label: 'T-Ball / Coach Pitch' },
                      { value: 'machinepitch', label: 'Machine Pitch' },
                      { value: 'playerspitch', label: 'Players Pitch' },
                    ]}
                  />
                  <p className="text-[10px] text-ink-2 mt-1">
                    Players Pitch unlocks pitch counts, xOVR &amp; reliever intel. Switching never deletes saved stats.
                  </p>
                </div>
              );
            })()}
            {/* Default Innings + League/Division moved to the Game Play section. */}

            <div className="pt-4 border-t border-hairline">
              <h4 className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest mb-4">Season & Scheduling</h4>
              <div className="grid grid-cols-2 gap-4">
                <InputField 
                  label="Season" 
                  value={teamInfo.season} 
                  onChange={(v: string) => updateTeamInfo('season', v)}
                  options={['Spring', 'Summer', 'Fall', 'Winter']}
                />
                <InputField
                  label="Year"
                  value={String(teamInfo.seasonYear)}
                  onChange={(v: string) => updateTeamInfo('seasonYear', Number(v) || new Date().getFullYear())}
                  options={['2024', '2025', '2026', '2027']}
                />
              </div>
              <div className="mt-4">
                <InputField
                  label="Team Timezone"
                  value={teamInfo.timezone}
                  onChange={(v: string) => updateTeamInfo('timezone', v)}
                  options={timezones}
                />
                <p className="text-[10px] text-ink-2 mt-1">Used for post-game reminder emails</p>
              </div>
              <div className="mt-4">
                <Toggle
                  label="Pre-Game Reminders"
                  description="A heads-up on your iPhone before first pitch. 8 hours out, or the evening before for morning games."
                  checked={settings.preGameRemindersEnabled}
                  onChange={(v: boolean) => updateSetting('preGameRemindersEnabled', v)}
                />
              </div>
              <div className="mt-4">
                <Toggle
                  label="Post-Game Scout Nudge"
                  description="A push after each game reminding you to upload the scorebook. Skipped automatically once the game's stats are in."
                  checked={settings.postGameNudgesEnabled}
                  onChange={(v: boolean) => updateSetting('postGameNudgesEnabled', v)}
                />
              </div>
              <div className="mt-4">
                <Toggle
                  label="Haptic Feedback"
                  description="Subtle taps on key moments: locking a lineup, starting a game, revealing an xOVR. iPhone only."
                  checked={settings.hapticsEnabled}
                  onChange={(v: boolean) => {
                    // Apply live so the very next buzz respects the change, then
                    // buzz once on turn-ON so the coach feels what they enabled.
                    setHapticsEnabled(v);
                    if (v) tapLight();
                    updateSetting('hapticsEnabled', v);
                  }}
                />
              </div>
            </div>

            {/* Calendar Import — folded into Team Info (no longer its own section). */}
            <div className="pt-4 border-t border-hairline">
              <h4 className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest mb-3">Calendar Import</h4>
              <div className="flex items-center justify-between gap-3">
                <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">
                  Sync games &amp; practices from your team calendar automatically.
                </p>
                <button
                  onClick={() => setIsImportingCalendar(true)}
                  className="shrink-0 px-4 py-2 bg-surface border border-hairline rounded-xl text-[10px] font-bold text-ink-2 uppercase tracking-widest hover:text-ink transition-colors"
                >
                  Connect
                </button>
              </div>
            </div>
          </div>
        </Section>

        {/* Game Play — competition context + on-field defaults. */}
        <Section id="game-play" title="Game Play" subtitle="League, division & game defaults" expandedSections={expandedSections} setExpandedSections={setExpandedSections}>
          <div className="space-y-4">
            <InputField
              label="League / Organization"
              value={teamInfo.org}
              onChange={(v: string) => updateTeamInfo('org', v)}
              options={ORG_OPTIONS}
            />
            <div className="grid grid-cols-2 gap-4">
              {(() => {
                const presets = ORG_DIVISIONS[teamInfo.org] ?? [];
                const isOther = presets.length > 0 && teamInfo.division !== '' && !presets.includes(teamInfo.division);
                const dropdownValue = isOther ? 'other' : teamInfo.division;
                if (presets.length === 0) {
                  return (
                    <InputField
                      label="Division"
                      value={teamInfo.division}
                      onChange={(v: string) => updateTeamInfo('division', v)}
                      placeholder="e.g. Majors"
                    />
                  );
                }
                return (
                  <div className="space-y-2">
                    <InputField
                      label="Division"
                      value={dropdownValue}
                      onChange={(v: string) => {
                        if (v === 'other') updateTeamInfo('division', '');
                        else updateTeamInfo('division', v);
                      }}
                      options={[
                        ...presets.map(p => ({ value: p, label: p })),
                        { value: 'other', label: 'Other…' },
                      ]}
                    />
                    {(isOther || dropdownValue === 'other') && (
                      <InputField
                        label="Custom Division"
                        value={teamInfo.division}
                        onChange={(v: string) => updateTeamInfo('division', v)}
                        placeholder="Type your division"
                      />
                    )}
                  </div>
                );
              })()}
              <InputField
                label="Default Innings"
                type="number"
                value={teamInfo.defaultInnings}
                onChange={(v: string) => updateTeamInfo('defaultInnings', parseInt(v) || 6)}
              />
            </div>

            {/* Game Clock — a game-day default, moved here from Field Time. */}
            <div className="pt-2 border-t border-hairline">
              <Toggle
                label="Game Clock"
                description="Show a countdown timer for timed games in the app and, on iPhone, the Dynamic Island. You'll be asked once when you start a game; change it here anytime."
                checked={settings.gameClockEnabled}
                onChange={(v: boolean) => updateSetting('gameClockEnabled', v)}
              >
                <InputField
                  label="Default Game Length (minutes)"
                  value={settings.defaultGameClockMinutes}
                  onChange={(v: string) => updateSetting('defaultGameClockMinutes', parseInt(v) || 0)}
                  type="number"
                  placeholder="75"
                />
              </Toggle>
            </div>
          </div>
        </Section>

        <Section
          id="field-time"
          title="Field Time"
          subtitle="Fairness rules for playing time"
          badge={`${[
            settings.fieldTimeEqualizerEnabled,
            settings.minFieldInningsEnabled,
            settings.equalBenchInnings,
            settings.noConsecutiveBench,
            settings.noConsecutivePosition,
            settings.mustPlayInfieldEnabled,
          ].filter(Boolean).length} rules active`}
          expandedSections={expandedSections}
          setExpandedSections={setExpandedSections}
        >
          <div className="space-y-2">

            <Toggle
              label="Field Time Equalizer"
              description="Rotates bench and defensive positions to ensure every player gets fair time on the field. Uses season history across games, not just this game."
              checked={settings.fieldTimeEqualizerEnabled}
              onChange={(v: boolean) => updateSetting('fieldTimeEqualizerEnabled', v)}
            />

            <div className={`pt-4 mt-2 border-t border-hairline space-y-2 transition-opacity ${settings.fieldTimeEqualizerEnabled ? '' : 'opacity-30 pointer-events-none'}`}>
              <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest pb-2">
                Rotation Rules
              </p>

              <Toggle
                label="Minimum Fielding Innings Per Game"
                description="Hard enforcement. App will force a fair lineup mercilessly: every player reaches this minimum before anyone plays extra."
                checked={settings.minFieldInningsEnabled}
                onChange={(v: boolean) => updateSetting('minFieldInningsEnabled', v)}
              >
                <InputField
                  label="Minimum Innings"
                  value={settings.minFieldInningsPerPlayer}
                  onChange={(v: string) => updateSetting('minFieldInningsPerPlayer', parseInt(v) || 0)}
                  type="number"
                  placeholder="3"
                />
              </Toggle>

              <Toggle
                label="Equal Bench Innings"
                description="Everyone sits out once before anyone sits out twice. Keeps bench time balanced across your whole roster."
                checked={settings.equalBenchInnings}
                onChange={(v: boolean) => updateSetting('equalBenchInnings', v)}
              />

              <Toggle
                label="No Repeat Bench"
                description="No player sits on the bench two innings in a row. The engine will always rotate a different player to the bench each inning."
                checked={settings.noConsecutiveBench}
                onChange={(v: boolean) => updateSetting('noConsecutiveBench', v)}
              />

              <Toggle
                label="No Repeat Positions"
                description="No player plays the same defensive position two innings in a row."
                checked={settings.noConsecutivePosition}
                onChange={(v: boolean) => updateSetting('noConsecutivePosition', v)}
              />

              <Toggle
                label="Must Play Infield"
                description="Every player must log at least this many innings at an infield position (P, C, 1B, 2B, SS, 3B) per game."
                checked={settings.mustPlayInfieldEnabled}
                onChange={(v: boolean) => updateSetting('mustPlayInfieldEnabled', v)}
              >
                <InputField
                  label="Infield Innings Required"
                  value={settings.mustPlayInfieldInnings}
                  onChange={(v: string) => updateSetting('mustPlayInfieldInnings', parseInt(v) || 0)}
                  type="number"
                  placeholder="1"
                />
              </Toggle>
            </div>

            {/* Which record a game's field time comes from: the Stats tab, the
                field-time report and Field Time Fairness all follow it, so it is
                not dimmed with the Equalizer. Pitching stats never depend on it. */}
            <div className="pt-4 mt-2 border-t border-hairline space-y-1.5">
              <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest pb-2">
                Records
              </p>
              <InputField
                label="Field Positions Come From"
                value={settings.fieldTimeSource}
                onChange={(v: string) => updateFieldTimeSource(v as FieldTimeSource)}
                options={[
                  { value: 'gamechanger', label: 'GameChanger imports' },
                  { value: 'dugout', label: 'Dugout live games' },
                ]}
              />
              <p className="text-[10px] font-medium text-ink-2 leading-relaxed px-1">
                {settings.fieldTimeSource === 'dugout'
                  ? 'Games you ran live in Dugout use the positions you tapped in, inning by inning. GameChanger fills in the games you did not track. Pitching stats always come from GameChanger.'
                  : 'Field time comes from your GameChanger imports. Games without an import use the positions you tracked live in Dugout. Pick Dugout live games if your scorekeeper does not keep up with defensive changes.'}
              </p>
            </div>

            <div className="pt-4 mt-2 border-t border-hairline space-y-2">
              <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest pb-2">
                Recognition
              </p>

              <Toggle
                label="Track Game Balls"
                description="After each game, award a game ball to standout players. The app tracks season totals so you can recognize players who haven't received one yet."
                checked={settings.trackGameBalls}
                onChange={(v: boolean) => updateSetting('trackGameBalls', v)}
              />

              {/* Where the season tally lives. Read-only note, not a link. */}
              {settings.trackGameBalls && (
                <p className="px-4 text-[10px] font-semibold text-ink-2 uppercase tracking-widest">
                  View recipients: Stats → Fairness
                </p>
              )}

              {/* Game Clock moved to the Game Play section (it's a game-day default,
                  not a field-time/recognition rule). */}

            </div>

          </div>
        </Section>

        {/* Field Configuration — only relevant to younger formats (5U–8U), where
            leagues drop the pitcher/catcher or add a 4th outfielder. Hidden for 9U+
            (kid-pitch), which always uses the standard diamond. Gated on the live
            ageGroup so it shows/hides the moment Age Group changes. */}
        {!isPlayerPitchAge(teamInfo.ageGroup) && (
        <Section id="field" title="Field Configuration" expandedSections={expandedSections} setExpandedSections={setExpandedSections}>
          <Toggle
            label="4 Outfielders"
            description="Splits CF into LCF and RCF. Common for younger leagues."
            checked={settings.fourOutfielders}
            onChange={(v: boolean) => updateSetting('fourOutfielders', v)}
          />
          <Toggle
            label="No Pitcher (Coach Pitch)"
            description="Removes the pitcher position from the field."
            checked={settings.noPitcher}
            onChange={(v: boolean) => updateSetting('noPitcher', v)}
          />
          <Toggle
            label="No Catcher"
            description="Removes the catcher position. Common in tee-ball."
            checked={settings.noCatcher}
            onChange={(v: boolean) => updateSetting('noCatcher', v)}
          />
        </Section>
        )}

        {/* Batting */}
        <Section id="batting" title="Batting" expandedSections={expandedSections} setExpandedSections={setExpandedSections}>
          <div className="space-y-4">
            {/* Batting Order Style maps to the three formats coaches actually use.
                It controls two engine levers only: how many present players bat,
                and whether an Extra Hitter (EH) batting-only slot exists. The
                legacy values 'Everyone Bats'/'Traditional 9 Players' are migrated
                to these on read (see settings init below). */}
            <div className="space-y-1.5">
              <InputField
                label="Batting Order Style"
                value={settings.battingOrderStyle}
                onChange={(v: string) => updateSetting('battingOrderStyle', v)}
                options={[
                  { value: 'Continuous', label: 'Continuous (everyone bats)' },
                  { value: '10 Players', label: '10 Players (9 field + EH)' },
                  { value: '9 Players',  label: '9 Players' },
                ]}
              />
              <p className="text-[10px] font-medium text-ink-2 leading-relaxed px-1">
                {settings.battingOrderStyle === 'Continuous'
                  ? 'Every player present bats, in one rotating order. Most common in youth ball.'
                  : settings.battingOrderStyle === '10 Players'
                  ? 'Ten batters: the nine fielders plus one Extra Hitter (EH). Everyone else is a substitute.'
                  : 'Nine batters: your fielders. Add a DH below if your pitcher does not hit.'}
              </p>
            </div>
            {settings.battingOrderStyle === '9 Players' && (
              <Toggle
                label="Use DH"
                description="Designated Hitter bats in the pitcher's spot. The pitcher does not bat."
                checked={settings.useDH}
                onChange={(v: boolean) => updateSetting('useDH', v)}
              />
            )}
            {/* Default Batting Strategy draws from the canonical ORDER_MODES list
                (the same names the live Lineup Builder shows), but only the modes
                the auto-lineup engine (sortBattingOrder) can actually run as a
                stored DEFAULT. The two data-driven modes — Get On Base / Wear Down
                — are excluded here: they need imported GameChanger stats and only
                run in Plan Game's preview, so as a saved default they'd silently
                do nothing. Coaches can still pick them per-game in the builder. */}
            {(() => {
              const DEFAULTABLE = new Set(['None', 'Optimized', 'Fairness', 'Minimize Strikeouts', 'Distribute Weaker Hitters']);
              const strategyOptions = ORDER_MODES.filter(m => DEFAULTABLE.has(m.key));
              return (
                <div className="space-y-1.5">
                  <InputField
                    label="Default Batting Strategy"
                    value={settings.battingStrategy}
                    onChange={(v: string) => updateSetting('battingStrategy', v)}
                    options={strategyOptions.map(m => ({ value: m.key, label: m.label }))}
                  />
                  <p className="text-[10px] font-medium text-ink-2 leading-relaxed px-1">
                    {ORDER_MODE_DESCRIPTIONS[settings.battingStrategy] || 'Applied automatically when the Lineup Builder opens. Can be overridden per game.'}
                  </p>
                </div>
              );
            })()}
            {(settings.battingOrderStyle === '10 Players' || settings.battingOrderStyle === '9 Players') && (
              <Toggle
                label="Mandatory At-Bat"
                description="Every player must have at least one plate appearance."
                checked={settings.mandatoryAB}
                onChange={(v: boolean) => updateSetting('mandatoryAB', v)}
              />
            )}
            {settings.battingOrderStyle === 'Continuous' && (
              <Toggle
                label="Entire Season Lineup"
                description="Keep the same batting order all season. Start games where the last game left off."
                checked={settings.entireSeasonLineup}
                onChange={(v: boolean) => updateSetting('entireSeasonLineup', v)}
              />
            )}
          </div>
        </Section>

        {/* Pitching Rules — pitch counts & rest only apply to Players Pitch. Hidden
            for T-Ball/Coach Pitch/Machine Pitch (no pitch counts there). */}
        {teamInfo.styleOfPlay === 'playerspitch' && (
        <Section
          id="pitching-rules"
          title="Pitching Rules"
          subtitle="Season limits & rest requirements"
          expandedSections={expandedSections}
          setExpandedSections={setExpandedSections}
        >
          <div className="space-y-2">

            {(() => {
              const leagueRuleset = PITCH_COUNT_RULESETS[teamInfo.org];
              const ageKey = settings.pitchCountAgeGroup || teamInfo.ageGroup || '12U';
              const ageRules = leagueRuleset?.ageGroups?.[ageKey];
              const rulesSummary = leagueRuleset && ageRules
                ? `${leagueRuleset.name} · ${ageKey} · ${
                    ageRules.logicType === 'innings' ? `${(ageRules as any).dailyMaxInnings} innings/day`
                    : ageRules.logicType === 'outs'  ? `${(ageRules as any).dailyMaxOuts} outs/day`
                    : `${ageRules.dailyMax} pitches/day`
                  }`
                : null;
              return (
                <Toggle
                  label="Use League Rules"
                  description={
                    rulesSummary
                      ? `Dugout will track ${leagueRuleset.name} pitch limits for ${ageKey} and alert you when a pitcher is approaching or has exceeded a threshold. The auto-lineup will skip ineligible pitchers, but you can always assign them manually.`
                      : teamInfo.org === 'independent' || !teamInfo.org
                        ? 'Set a league / organization in Team Information to enable this.'
                        : 'Select an age group in Team Information to see the limit.'
                  }
                  checked={settings.enforcePitchCounts && !!leagueRuleset}
                  onChange={(v: boolean) => {
                    if (v && leagueRuleset) {
                      const next = { ...settings, enforcePitchCounts: true, pitchCountRuleset: teamInfo.org };
                      setSettings(next);
                      setDoc(doc(db, 'teams', team.id), { settings: next }, { merge: true });
                    } else {
                      updateSetting('enforcePitchCounts', false);
                    }
                  }}
                >
                  {rulesSummary && (
                    <div className="px-4 py-3 bg-canvas border border-hairline rounded-xl">
                      <p className="text-[11px] font-semibold text-accent-ink uppercase tracking-widest">{rulesSummary}</p>
                    </div>
                  )}
                </Toggle>
              );
            })()}

            <div className="pb-4 mb-2 border-b border-hairline">
              <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Custom Overrides</p>
            </div>

            <Toggle
              label="Max Pitches Per Day"
              description="Warning shown when pitcher reaches this limit. Coach can override."
              checked={settings.maxPitchesPerDayEnabled}
              onChange={(v: boolean) => updateSetting('maxPitchesPerDayEnabled', v)}
            >
              <InputField
                label="Max Pitches"
                value={settings.maxPitchesPerDay}
                onChange={(v: string) => updateSetting('maxPitchesPerDay', parseInt(v) || 0)}
                type="number"
                placeholder="75"
              />
            </Toggle>

            <Toggle
              label="Max Innings Per Game"
              description="Warning when a pitcher reaches this inning count in a single game."
              checked={settings.maxInningsPerGameEnabled}
              onChange={(v: boolean) => updateSetting('maxInningsPerGameEnabled', v)}
            >
              <InputField
                label="Max Innings"
                value={settings.maxInningsPerGame}
                onChange={(v: string) => updateSetting('maxInningsPerGame', parseInt(v) || 0)}
                type="number"
                placeholder="3"
              />
            </Toggle>

            <Toggle
              label="Max Innings Per Day"
              description="Warning when a pitcher's total innings across games in a day reaches this limit."
              checked={settings.maxInningsPerDayEnabled}
              onChange={(v: boolean) => updateSetting('maxInningsPerDayEnabled', v)}
            >
              <InputField
                label="Max Innings"
                value={settings.maxInningsPerDay}
                onChange={(v: string) => updateSetting('maxInningsPerDay', parseInt(v) || 0)}
                type="number"
                placeholder="6"
              />
            </Toggle>

            <Toggle
              label="Max Innings Per Week"
              description="Warning when a pitcher's total innings in the past 7 days reaches this limit."
              checked={settings.maxInningsPerWeekEnabled}
              onChange={(v: boolean) => updateSetting('maxInningsPerWeekEnabled', v)}
            >
              <InputField
                label="Max Innings"
                value={settings.maxInningsPerWeek}
                onChange={(v: string) => updateSetting('maxInningsPerWeek', parseInt(v) || 0)}
                type="number"
                placeholder="9"
              />
            </Toggle>

            <Toggle
              label="Max Appearances Per 3 Days"
              description="Warning when a pitcher has appeared in this many games within the last 3 calendar days."
              checked={settings.maxAppearancesPer3DaysEnabled}
              onChange={(v: boolean) => updateSetting('maxAppearancesPer3DaysEnabled', v)}
            >
              <InputField
                label="Max Appearances"
                value={settings.maxAppearancesPer3Days}
                onChange={(v: string) => updateSetting('maxAppearancesPer3Days', parseInt(v) || 0)}
                type="number"
                placeholder="2"
              />
            </Toggle>

            <Toggle
              label="No Consecutive Days Pitching"
              description="Warning when a pitcher threw yesterday and is being assigned again today."
              checked={settings.noConsecutiveDaysPitching}
              onChange={(v: boolean) => updateSetting('noConsecutiveDaysPitching', v)}
            />

            <Toggle
              label="Catcher Can't Pitch"
              description="A player who catches in any inning of a game is not eligible to pitch in that same game."
              checked={settings.catcherToPitcherRestriction}
              onChange={(v: boolean) => updateSetting('catcherToPitcherRestriction', v)}
            />

            <Toggle
              label="Pitcher Can't Catch"
              description="A player who pitches in a game is not eligible to catch for the remainder of that game."
              checked={settings.pitcherToCatcherRestriction}
              onChange={(v: boolean) => updateSetting('pitcherToCatcherRestriction', v)}
            />

            <Toggle
              label="Pitch Count Blocks Catching"
              description="After reaching the pitch threshold, a pitcher cannot return as catcher."
              checked={settings.pitchCountBlocksCatchingEnabled}
              onChange={(v: boolean) => updateSetting('pitchCountBlocksCatchingEnabled', v)}
            >
              <InputField
                label="Pitch Threshold"
                value={settings.pitchCountBlocksCatching}
                onChange={(v: string) => updateSetting('pitchCountBlocksCatching', parseInt(v) || 0)}
                type="number"
                placeholder="40"
              />
            </Toggle>

            <Toggle
              label="Tournaments Operate Independently"
              description="Tournament games ignore season rest rules. Only tournament-level pitch caps apply."
              checked={settings.tournamentsOperateIndependently}
              onChange={(v: boolean) => updateSetting('tournamentsOperateIndependently', v)}
            />

          </div>
        </Section>
        )}

        {/* Tournaments — tournament org & pitch rules; like Pitching Rules, only
            meaningful for Players Pitch. Hidden for non-player-pitch styles. */}
        {teamInfo.styleOfPlay === 'playerspitch' && (
        <Section
          id="tournaments"
          title="Tournaments"
          subtitle="Tournament org & pitch rules"
          expandedSections={expandedSections}
          setExpandedSections={setExpandedSections}
        >
          <div className="space-y-4">
            <div>
              <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest pb-2">Tournament Organization</p>
              <InputField
                label="Org / League"
                value={settings.tournamentOrg}
                onChange={(v: string) => updateSetting('tournamentOrg', v)}
                options={[
                  { value: 'little_league',  label: 'Little League' },
                  { value: 'usssa',          label: 'USSSA' },
                  { value: 'perfect_game',   label: 'Perfect Game' },
                  { value: 'nfhs',           label: 'NFHS (High School)' },
                  { value: 'custom',         label: 'Custom' },
                ]}
              />
            </div>

            {settings.tournamentOrg !== 'custom' && PITCH_COUNT_RULESETS[settings.tournamentOrg] && (
              <div className="p-4 bg-surface border border-hairline rounded-xl space-y-2">
                <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">
                  {PITCH_COUNT_RULESETS[settings.tournamentOrg].name}: Rules Summary
                </p>
                <p className="text-[11px] text-ink-2 leading-relaxed">
                  {PITCH_COUNT_RULESETS[settings.tournamentOrg].description}
                </p>
                {PITCH_COUNT_RULESETS[settings.tournamentOrg].advisory && (
                  <span className="inline-block px-2 py-0.5 bg-gold-soft text-gold-ink text-[9px] font-bold rounded uppercase tracking-wider">
                    Advisory Only
                  </span>
                )}
              </div>
            )}

            {settings.tournamentOrg === 'custom' && (
              <div className="space-y-4">
                <div className="pt-2 border-t border-hairline">
                  <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest pb-3">Custom Tournament Rules</p>
                  <div className="space-y-4">
                    <InputField
                      label="Tracking Unit"
                      value={settings.customTournamentTracking}
                      onChange={(v: string) => updateSetting('customTournamentTracking', v)}
                      options={[
                        { value: 'pitches', label: 'Pitches' },
                        { value: 'innings', label: 'Innings' },
                        { value: 'outs',    label: 'Outs' },
                      ]}
                    />
                    <InputField
                      label="Daily Limit"
                      type="number"
                      value={settings.customTournamentDailyLimit}
                      onChange={(v: string) => updateSetting('customTournamentDailyLimit', parseInt(v) || 0)}
                      placeholder="85"
                    />
                    <InputField
                      label="Event / Tournament Limit (0 = no limit)"
                      type="number"
                      value={settings.customTournamentEventLimit}
                      onChange={(v: string) => updateSetting('customTournamentEventLimit', parseInt(v) || 0)}
                      placeholder="0"
                    />
                    <Toggle
                      label="No Rest Days Required"
                      description="Pitchers can appear any day without enforced rest periods."
                      checked={settings.customTournamentNoRestDays}
                      onChange={(v: boolean) => updateSetting('customTournamentNoRestDays', v)}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </Section>
        )}

        {/* Display Settings — UI preferences; placed near the bottom, below the
            core team/game config sections. */}
        <Section id="display" title="Display Settings" expandedSections={expandedSections} setExpandedSections={setExpandedSections}>
          <div className="space-y-6">
            {/* Dark Mode toggle hidden until real dark mode ships — see note on the
                duplicate Display Settings section above. */}
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-[10px] font-semibold text-ink-2 uppercase tracking-widest mb-1">Show Record on Dashboard</label>
                <p className="text-[10px] text-ink-2">Display the team's W-L-T record on the home page.</p>
              </div>
              <button
                onClick={() => updateSetting('showRecordOnDashboard', !settings.showRecordOnDashboard)}
                className={cn(
                  "w-12 h-6 rounded-full transition-colors relative",
                  settings.showRecordOnDashboard ? "bg-emerald-500" : "bg-track"
                )}
              >
                <div className={cn(
                  "w-4 h-4 bg-surface rounded-full absolute top-1 transition-transform",
                  settings.showRecordOnDashboard ? "translate-x-7" : "translate-x-1"
                )} />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <label className="block text-[10px] font-semibold text-ink-2 uppercase tracking-widest mb-1">Show Team OVR in Header</label>
                <p className="text-[10px] text-ink-2">Display the team's average OVR or xOVR badge in the top bar.</p>
              </div>
              <button
                onClick={() => updateSetting('showTeamOvrInHeader', !settings.showTeamOvrInHeader)}
                className={cn(
                  "w-12 h-6 rounded-full transition-colors relative shrink-0 ml-4",
                  settings.showTeamOvrInHeader ? "bg-emerald-500" : "bg-track"
                )}
              >
                <div className={cn(
                  "w-4 h-4 bg-surface rounded-full absolute top-1 transition-transform",
                  settings.showTeamOvrInHeader ? "translate-x-7" : "translate-x-1"
                )} />
              </button>
            </div>
          </div>
        </Section>

        {/* Assistant Coach Access — share code + xOVR blending (head coach only) */}
        {isHeadCoach && <Section
          id="assistant-access"
          title="Assistant Coach Access"
          subtitle="Share code & xOVR blending"
          expandedSections={expandedSections}
          setExpandedSections={setExpandedSections}
        >
          {/* Share Code */}
          <div className="mb-6">
            <div className="flex items-center space-x-2 mb-1">
              <KeyRound className="w-4 h-4 text-accent-ink" />
              <h3 className="font-black uppercase text-sm text-ink">Share Code</h3>
            </div>
            <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest mb-4">
              Share this code with assistant coaches so they can join your team.
            </p>
            {joinCode && (
              <div className="flex items-center gap-3">
                <div className="flex-1 bg-surface border border-ink-mute rounded-xl px-4 py-3 font-black text-xl text-center uppercase tracking-widest text-ink">
                  {joinCode}
                </div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(joinCode);
                    setCopiedCode(true);
                    setTimeout(() => setCopiedCode(false), 2000);
                  }}
                  className="px-4 py-3 bg-ink hover:bg-ink text-surface rounded-xl font-black text-xs uppercase tracking-widest transition-colors flex items-center gap-2"
                >
                  {copiedCode ? <CheckIcon className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copiedCode ? 'Copied!' : 'Copy'}
                </button>
              </div>
            )}
          </div>

          <Toggle
            label="Include Assistant Coach's Player Attributes in xOVR"
            description="Blends your assistant's player ratings with yours. xOVR is calculated from the average of both scouts. Your ratings and your assistant's ratings stay independent."
            checked={settings.includeAssistantScouting}
            onChange={async (v: boolean) => {
              await updateSetting('includeAssistantScouting', v);
              // Batch-recompute OVR for all players that have assistant ratings
              const batch = writeBatch(db);
              let hasUpdates = false;
              players.forEach(player => {
                if (!player.assistantRatings || Object.keys(player.assistantRatings).length === 0) return;
                const headCoachOVR = calculateOVR(player.attributes, player.primaryPosition);
                const blended = blendScoutingOVR(headCoachOVR, player.assistantRatings, v);
                const breakdown = calculateXOVR(blended, player.seasonSnapshot?.stats ?? player.stats ?? null);
                batch.update(doc(db, `teams/${team.id}/players`, player.id), {
                  ovr: breakdown.xovr,
                  ovrBreakdown: breakdown,
                });
                hasUpdates = true;
              });
              if (hasUpdates) await batch.commit();
            }}
          />
        </Section>}

        {/* Help & Support */}
        <Section
          id="support"
          title="Help & Support"
          subtitle="Report a bug or send feedback"
          expandedSections={expandedSections}
          setExpandedSections={setExpandedSections}
        >
          <div className="py-2 space-y-4">
            <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">
              Found a bug? Have a feature idea? Send us a message and we'll get back to you.
            </p>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setIsShowingSupportForm(true)}
                className="px-6 py-3 bg-surface border border-hairline rounded-xl font-black text-xs uppercase tracking-widest text-ink-2 hover:text-ink hover:border-ink-mute transition-colors"
              >
                Contact Support
              </button>
              <button
                onClick={() => setShowTerms(true)}
                className="px-6 py-3 bg-surface border border-hairline rounded-xl font-black text-xs uppercase tracking-widest text-ink-2 hover:text-ink hover:border-ink-mute transition-colors"
              >
                Terms of Use
              </button>
              <button
                onClick={() => setShowPrivacy(true)}
                className="px-6 py-3 bg-surface border border-hairline rounded-xl font-black text-xs uppercase tracking-widest text-ink-2 hover:text-ink hover:border-ink-mute transition-colors"
              >
                Privacy Policy
              </button>
            </div>

            {myTickets.length > 0 && (
              <div className="space-y-2 pt-2">
                <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">My Tickets</p>
                {myTickets.map((ticket: any) => (
                  <div key={ticket.id} className="bg-raised border border-hairline rounded-2xl p-4">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-xs font-black text-ink leading-tight">{ticket.subject}</p>
                      <span className={cn(
                        "text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full shrink-0",
                        ticket.status === 'in_progress' ? 'bg-gold-soft text-gold-ink' : 'bg-hairline/60 text-ink-2'
                      )}>
                        {ticket.status === 'in_progress' ? 'In Progress' : 'Open'}
                      </span>
                    </div>
                    <p className="text-[10px] text-ink-2 mt-0.5">{ticket.category} · {ticket.createdAt?.slice(0, 10)}</p>
                    {ticket.adminNote && (
                      <p className="mt-2 text-[10px] text-ink-2 bg-canvas rounded-xl px-3 py-2 leading-relaxed">
                        <span className="font-black text-ink-2 uppercase tracking-widest">Note: </span>{ticket.adminNote}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Section>

        {/* Season History — past seasons preserved via the Start New Season rollover (head coach only) */}
        {isHeadCoach && (
          <div className="mt-12 pt-12 border-t border-hairline">
            <div className="flex items-center justify-between gap-4 mb-6">
              <div className="flex items-center space-x-2">
                <Archive className="w-5 h-5 text-accent-ink" />
                <h3 className="font-black uppercase text-lg italic text-ink">Season History</h3>
              </div>
              <button
                onClick={openNewSeasonModal}
                className="px-5 py-2.5 bg-emerald-500 text-surface rounded-xl font-black text-xs uppercase italic hover:bg-emerald-400 transition-all flex items-center justify-center space-x-2 shrink-0"
              >
                <Archive className="w-4 h-4" />
                <span>Start New Season</span>
              </button>
            </div>

            {archivedKeys.size > 0 ? (
              <div className="bg-surface shadow-card rounded-card p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div>
                    <div className="font-black uppercase text-sm text-ink mb-1">Past Seasons</div>
                    <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest max-w-md">
                      Review past seasons (games, stats, and leaderboards) any time. Read-only.
                    </p>
                  </div>
                  <button
                    onClick={() => onOpenSeasonHistory?.()}
                    className="px-6 py-3 bg-canvas border border-hairline text-ink rounded-xl font-black text-xs uppercase italic hover:border-emerald-500 hover:bg-emerald-500/10 transition-all flex items-center justify-center space-x-2 shrink-0"
                  >
                    <Archive className="w-4 h-4" />
                    <span>View Season History</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-surface shadow-card rounded-card p-6">
                <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest max-w-md leading-relaxed">
                  After you begin a new season, every prior season's history is preserved and accessed here.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Season Wrapped — end-of-season story (head coach only) */}
        {isHeadCoach && (
          <div className="mt-12 pt-12 border-t border-hairline">
            <div className="flex items-center space-x-2 mb-6">
              <Sparkles className="w-5 h-5 text-gold-ink" />
              <h3 className="font-black uppercase text-lg italic text-ink">Season Wrapped</h3>
            </div>
            <div className="bg-gradient-to-br from-[#1C2127] to-ink rounded-2xl p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                  <div className="font-black uppercase text-sm text-surface mb-1">Your season, wrapped up</div>
                  <p className="text-[10px] font-semibold text-[#9AA0A6] uppercase tracking-widest max-w-md leading-relaxed">
                    A shareable story of the team's season. The record, the leaders, the biggest moment, and a card for every player who earned one.
                  </p>
                </div>
                <button
                  onClick={() => onOpenWrapped?.()}
                  className="px-6 py-3 bg-[#FCD34D] text-ink rounded-xl font-black text-xs uppercase italic hover:brightness-105 transition-all flex items-center justify-center space-x-2 shrink-0"
                >
                  <Sparkles className="w-4 h-4" />
                  <span>See Your Wrapped</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Advanced — destructive/admin actions, collapsed by default (head coach only) */}
        {isHeadCoach && (
        <div className="mt-8">
        <Section id="advanced" title="Advanced" subtitle="Clear stats, delete team" expandedSections={expandedSections} setExpandedSections={setExpandedSections}>
          <div className="bg-[#FBF0F0] border border-hairline rounded-2xl p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div>
                <div className="font-black uppercase text-sm text-ink mb-1">Clear This Season's Stats</div>
                <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest max-w-md">
                  Erases every stat Dugout has for {seasonLabel}, so you can start over or re-import cleanly. Your roster, schedule and scores stay.
                </p>
                <p className="text-[10px] font-semibold text-danger uppercase tracking-widest mt-1 flex items-center gap-1.5">
                  <AlertTriangle className="w-3 h-3 shrink-0" />
                  <span>This action cannot be undone.</span>
                </p>

                {/* The confirm spells out exactly what goes and what stays, since
                    the old button's vague promise is what misled ticket #1. */}
                {clearPlan && (
                  <div className="mt-4 space-y-3 max-w-md">
                    <div>
                      <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest mb-1.5">Erased for {seasonLabel}</p>
                      <ul className="text-xs font-medium text-ink leading-relaxed list-disc pl-4 space-y-0.5">
                        <li>
                          {clearPlan.statsGameCount > 0
                            ? `Player stats from ${clearPlan.statsGameCount} ${clearPlan.statsGameCount === 1 ? 'game' : 'games'}`
                            : "Each player's season totals"}
                          {clearPlan.signupGameId ? ', plus the season stats you imported at signup' : ''}
                        </li>
                        <li>GameChanger field time, play-by-play and game insights</li>
                        <li>Every pitch count and each player's pitching history</li>
                        {clearPlan.clearStartRecord && (
                          <li>
                            The starting record you entered at signup ({team.seasonStartRecord?.w ?? 0}-{team.seasonStartRecord?.l ?? 0}{team.seasonStartRecord?.t ? `-${team.seasonStartRecord.t}` : ''})
                          </li>
                        )}
                      </ul>
                    </div>
                    <p className="text-xs font-medium text-ink-2 leading-relaxed">
                      Kept: your roster and player ratings, the schedule, game scores, past seasons, and the positions and game balls you tracked in Dugout.
                      {clearPlan.skippedLiveGameIds.length > 0 ? ' A game in progress is left alone.' : ''}
                    </p>
                    <p className="text-xs font-semibold text-danger leading-relaxed flex items-start gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span>Pitcher rest starts over. Every pitcher will show as fully rested until you re-import or re-enter pitch counts, so check rest days yourself before your next game.</span>
                    </p>
                  </div>
                )}
              </div>

              {!showClearConfirm ? (
                <button 
                  onClick={() => setShowClearConfirm(true)}
                  className="px-6 py-3 bg-canvas border border-hairline text-danger rounded-xl font-black text-xs uppercase italic hover:bg-red-500 hover:text-ink transition-all flex items-center justify-center space-x-2"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Clear Stats</span>
                </button>
              ) : (
                <div className="flex items-center space-x-3">
                  <button 
                    onClick={() => setShowClearConfirm(false)}
                    className="px-4 py-3 bg-raised text-ink-2 rounded-xl font-black text-xs uppercase italic hover:text-ink transition-colors"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleClearAllStats}
                    disabled={isClearingStats}
                    className="px-6 py-3 bg-red-500 text-ink rounded-xl font-black text-xs uppercase italic hover:bg-red-600 transition-colors flex items-center space-x-2 shadow-[0_0_20px_rgba(239,68,68,0.2)]"
                  >
                    {isClearingStats ? (
                      <div className="w-4 h-4 border-2 border-ink/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                    <span>{isClearingStats ? 'Erasing...' : 'Erase Stats'}</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="bg-[#FBF0F0] border border-hairline rounded-2xl p-6 mt-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div>
                <div className="font-black uppercase text-sm text-ink mb-1">Delete Team</div>
                <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest max-w-md">
                  Permanently deletes this team, all players, all games, and all tournaments.
                  This does not cancel your Elite membership. Cancel that first via your User Account.
                  After deleting a team, you'll be prompted to create or join a team.
                </p>
                <p className="text-[10px] font-semibold text-danger uppercase tracking-widest mt-1 flex items-center gap-1.5">
                  <AlertTriangle className="w-3 h-3 shrink-0" />
                  <span>This action cannot be undone.</span>
                </p>
                {deleteTeamError && (
                  <p className="text-[10px] font-bold text-danger mt-2">{deleteTeamError}</p>
                )}
              </div>

              {!showDeleteTeamConfirm ? (
                <button
                  onClick={() => setShowDeleteTeamConfirm(true)}
                  className="px-6 py-3 bg-canvas border border-hairline text-danger rounded-xl font-black text-xs uppercase italic hover:bg-red-500 hover:text-ink transition-all flex items-center justify-center space-x-2 shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete Team</span>
                </button>
              ) : (
                <div className="flex items-center space-x-3 shrink-0">
                  <button
                    onClick={() => setShowDeleteTeamConfirm(false)}
                    className="px-4 py-3 bg-raised text-ink-2 rounded-xl font-black text-xs uppercase italic hover:text-ink transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteTeam}
                    disabled={isDeletingTeam}
                    className="px-6 py-3 bg-red-500 text-ink rounded-xl font-black text-xs uppercase italic hover:bg-red-600 transition-colors flex items-center space-x-2 shadow-[0_0_20px_rgba(239,68,68,0.2)] disabled:opacity-50"
                  >
                    {isDeletingTeam
                      ? <div className="w-4 h-4 border-2 border-ink/30 border-t-white rounded-full animate-spin" />
                      : <Trash2 className="w-4 h-4" />
                    }
                    <span>Yes, Delete Everything</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </Section>
        </div>
        )}

      <p className="text-center text-[10px] text-ink-2 mt-8 select-all">Team ID: {team.id}</p>
      {uid && <p className="text-center text-[10px] text-ink-2 pb-2 select-all">User ID: {uid}</p>}

      </div>

      {isImportingCalendar && (
        <CalendarImporter
          teamId={team.id}
          teamName={team.name}
          onClose={() => setIsImportingCalendar(false)}
        />
      )}

      {isShowingSupportForm && (
        <SupportForm
          teamId={team.id}
          teamName={team.name}
          userEmail={userEmail}
          uid={uid}
          onClose={() => setIsShowingSupportForm(false)}
        />
      )}

      {isAdmin && <AdminUsagePanel />}
      {isAdmin && <AdminTicketPanel />}

      {/* ── Start New Season — guided 3-step modal ─────────────────────────── */}
      <AnimatePresence>
        {showNewSeasonModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-surface/70 backdrop-blur-sm flex items-center justify-center p-6"
          >
            <motion.div
              initial={{ scale: 0.96, y: 8 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, y: 8 }}
              className="bg-canvas border border-hairline rounded-3xl max-w-md w-full p-6 space-y-5"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Archive className="w-5 h-5 text-accent-ink" />
                  <h3 className="font-black uppercase text-lg italic text-ink">Start New Season</h3>
                </div>
                {!isRollingSeason && (
                  <button onClick={() => setShowNewSeasonModal(false)} className="text-ink-2 hover:text-ink">
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>

              {/* Step 1 — Explain */}
              {newSeasonStep === 1 && !rolloverDone && (
                <div className="space-y-4">
                  <p className="text-sm text-ink-2 leading-relaxed">
                    This locks in your <span className="font-black">{team?.season} {team?.seasonYear}</span> book
                    (every stat, pitch log, and game ball) into your Season History, then wipes the slate clean
                    for the next one. Your whole bench rides along; nobody gets cut here.
                  </p>
                  <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-4 space-y-1">
                    <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Archiving</p>
                    <p className="text-sm font-black text-ink">{players.length} player{players.length !== 1 ? 's' : ''} · {team?.season} {team?.seasonYear}</p>
                    <p className="text-[10px] text-ink-2">Your games stay put and stay viewable in Season History.</p>
                  </div>
                  <ul className="space-y-2 text-[11px] text-ink-2">
                    <li className="flex items-start gap-2">
                      <CheckIcon className="w-3.5 h-3.5 text-accent-ink mt-0.5 shrink-0" />
                      <span>Every stat, pitch log, game, and game ball is preserved: <span className="font-black text-accent-ink">nothing is deleted</span>.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckIcon className="w-3.5 h-3.5 text-accent-ink mt-0.5 shrink-0" />
                      <span>Your whole roster carries over to the new season. Nobody gets cut here.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckIcon className="w-3.5 h-3.5 text-accent-ink mt-0.5 shrink-0" />
                      <span>Season stats reset to zero for a fresh start. Revisit prior seasons any time in Season History.</span>
                    </li>
                  </ul>
                  <button
                    onClick={() => setNewSeasonStep(2)}
                    className="w-full px-6 py-3 bg-emerald-500 text-surface rounded-xl font-black text-xs uppercase italic hover:bg-emerald-400 transition-all flex items-center justify-center gap-2"
                  >
                    <span>Next: Name the Season</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Step 2 — Choose new season */}
              {newSeasonStep === 2 && !rolloverDone && (
                <div className="space-y-4">
                  <p className="text-sm text-ink-2">What are we calling the new season?</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-semibold text-ink-2 uppercase tracking-widest mb-1">Season</label>
                      <select
                        value={newSeasonName}
                        onChange={(e) => setNewSeasonName(e.target.value)}
                        style={{ colorScheme: 'light' }}
                        className="w-full px-3 py-3 bg-surface border border-hairline rounded-xl text-sm font-bold text-ink"
                      >
                        {['Spring', 'Summer', 'Fall', 'Winter'].map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-ink-2 uppercase tracking-widest mb-1">Year</label>
                      <select
                        value={String(newSeasonYear)}
                        onChange={(e) => setNewSeasonYear(parseInt(e.target.value))}
                        style={{ colorScheme: 'light' }}
                        className="w-full px-3 py-3 bg-surface border border-hairline rounded-xl text-sm font-bold text-ink"
                      >
                        {(() => { const cy = Number(team?.seasonYear) || new Date().getFullYear(); return [cy - 1, cy, cy + 1, cy + 2]; })()
                          .filter((y, i, arr) => y && arr.indexOf(y) === i)
                          .map(y => <option key={y} value={String(y)}>{y}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Kids age up between seasons — make the coach verify the level. */}
                  <div>
                    <label className="block text-[10px] font-semibold text-ink-2 uppercase tracking-widest mb-1">Level of Play</label>
                    <select
                      value={newSeasonAgeGroup}
                      onChange={(e) => setNewSeasonAgeGroup(e.target.value)}
                      style={{ colorScheme: 'light' }}
                      className="w-full px-3 py-3 bg-surface border border-hairline rounded-xl text-sm font-bold text-ink"
                    >
                      {AGE_GROUPS.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                    <p className="text-[10px] text-ink-2 mt-1">
                      {newSeasonAgeGroup && newSeasonAgeGroup !== teamInfo.ageGroup
                        ? `Moving up from ${teamInfo.ageGroup || '—'}, so pitch count rules will follow the new level.`
                        : 'Kids age up. Double-check the level for the new season.'}
                    </p>
                  </div>

                  {targetIsCurrent && (
                    <p className="text-[10px] font-bold text-danger">
                      That's your current season. Pick a different season or year to roll forward.
                    </p>
                  )}
                  {!targetIsCurrent && targetAlreadyArchived && (
                    <p className="text-[10px] font-bold text-danger">
                      You already have a <span className="uppercase">{newSeasonName} {newSeasonYear}</span> in your
                      history. Starting it again would overwrite it. Choose another.
                    </p>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={() => setNewSeasonStep(1)}
                      className="px-4 py-3 bg-raised text-ink-2 rounded-xl font-black text-xs uppercase italic hover:text-ink transition-colors"
                    >
                      Back
                    </button>
                    <button
                      onClick={() => setNewSeasonStep(3)}
                      disabled={targetIsCurrent || targetAlreadyArchived}
                      className="flex-1 px-6 py-3 bg-emerald-500 text-surface rounded-xl font-black text-xs uppercase italic hover:bg-emerald-400 transition-all flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <span>Next: Confirm</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3 — Confirm + run */}
              {newSeasonStep === 3 && !rolloverDone && (
                <div className="space-y-4">
                  <p className="text-sm text-ink-2 leading-relaxed">
                    Archiving <span className="font-black">{team?.season} {team?.seasonYear}</span> and starting{' '}
                    <span className="font-black">{newSeasonName} {newSeasonYear}</span>
                    {newSeasonAgeGroup && newSeasonAgeGroup !== teamInfo.ageGroup && (
                      <> at <span className="font-black">{newSeasonAgeGroup}</span> (up from {teamInfo.ageGroup || '—'})</>
                    )}. Ready when you are, coach.
                  </p>
                  {rolloverError && (
                    <p className="text-[10px] font-bold text-danger">{rolloverError}</p>
                  )}
                  <div className="flex gap-3">
                    <button
                      onClick={() => setNewSeasonStep(2)}
                      disabled={isRollingSeason}
                      className="px-4 py-3 bg-raised text-ink-2 rounded-xl font-black text-xs uppercase italic hover:text-ink transition-colors disabled:opacity-50"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleStartNewSeason}
                      disabled={isRollingSeason}
                      className="flex-1 px-6 py-3 bg-emerald-500 text-surface rounded-xl font-black text-xs uppercase italic hover:bg-emerald-400 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {isRollingSeason
                        ? <div className="w-4 h-4 border-2 border-ink/30 border-t-white rounded-full animate-spin" />
                        : <Archive className="w-4 h-4" />}
                      <span>Archive &amp; Start New Season</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Success */}
              {rolloverDone && (
                <div className="space-y-4 text-center">
                  <div className="mx-auto w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center">
                    <CheckIcon className="w-6 h-6 text-surface" />
                  </div>
                  <p className="text-sm font-black text-ink">Welcome to {newSeasonName} {newSeasonYear}.</p>
                  <p className="text-[10px] text-ink-2">
                    Your {team?.season} {team?.seasonYear} season is safe in Season History. Fresh stats, same crew.
                  </p>
                  <button
                    onClick={() => setShowNewSeasonModal(false)}
                    className="w-full px-6 py-3 bg-emerald-500 text-surface rounded-xl font-black text-xs uppercase italic hover:bg-emerald-400 transition-all"
                  >
                    Let's Play Ball
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {showTerms && <TermsOfService onClose={() => setShowTerms(false)} />}
      {showPrivacy && <PrivacyPolicy onClose={() => setShowPrivacy(false)} />}
    </div>
  );
};